import {
  ButtonsTheme,
  Color,
  ColorPalette,
  TextColorPalette,
  Theme as ThemeProps,
  TypographyOptions,
  UISizes,
  UITheme,
} from './styles.interface';
import { CSS_BORDER_TYPE, CSS_PROPERTY, MAIN_COLOR_PALETTE, TEXT_COLOR_PALETTE, TEXT_SIZE_PALETTE } from '../enums/Css';
import {
  CcpaUISettings,
  Customization,
  DefaultUISettings,
  SECOND_LAYER_TRIGGER,
  TCFUISettings,
  UI_VARIANT,
} from '@usercentrics/cmp-browser-sdk';
import {
  DEFAULT_ACTIVE_TOGGLE,
  DEFAULT_BORDER_RADIUS,
  DEFAULT_DISABLED_TOGGLE,
  DEFAULT_FONT_FAMILY,
  DEFAULT_FONT_SIZE,
  DEFAULT_INACTIVE_TOGGLE,
  DEFAULT_LAYER_BORDER_RADIUS,
  DEFAULT_LAYER_COLOR,
  DEFAULT_LIGHT_THEME_ACCENT_COLOR,
  DEFAULT_LIGHT_THEME_FONT_COLOR,
  DEFAULT_LIGHT_THEME_NEUTRAL_COLOR,
  DEFAULT_MAX_BANNER_WIDTH,
  DEFAULT_MAX_SIDE_AND_CENTER_CONTAINER_WIDTH,
  DEFAULT_OVERLAY_COLOR,
  DEFAULT_OVERLAY_OPACITY,
  DEFAULT_TOGGLE_ICON,
  FONT_WEIGHT_BOLD,
  FONT_WEIGHT_LIGHT,
  FONT_WEIGHT_REGULAR,
  FONT_WEIGHT_SEMI_BOLD,
} from './sc-vars';
import { darken, meetsContrastGuidelines, tint } from 'polished';
import { spacing } from './spacing';

export { CSS_BORDER_TYPE, CSS_PROPERTY };
export { Color, Theme } from './styles.interface';
export { breakpoints } from './sc-vars';
export { media } from './media';
export { FONT_WEIGHT_BOLD, FONT_WEIGHT_REGULAR, FONT_WEIGHT_LIGHT, FONT_WEIGHT_SEMI_BOLD };

/**
 * This method will return the primary font color if it's contrast to the provided background color is deemed good
 * enough. If the contrast is deemed too bad, the quaternary font color will be returned.
 */
export function getHighContrastTextColor(backgroundColor: string, textColorPalette: TextColorPalette): string {
  if (meetsContrastGuidelines(backgroundColor, textColorPalette.primary).AA) {
    return textColorPalette.primary;
  }

  return textColorPalette.quaternary;
}

export const generateColor = (color: string): Color => {
  return {
    dark: darken(0.1, color),
    default: color,
  };
};

/**
 * This method will generate multiple font color gradations from a given primary color by tinting that primary color
 * by predefined factors.
 */
function generateColorPalette(primaryColor: string): ColorPalette {
  const secondaryColor = tint(0.2, primaryColor);
  const tertiaryColor = tint(0.84, primaryColor);
  const quaternaryColor = tint(0.98, primaryColor);

  return {
    [MAIN_COLOR_PALETTE.PRIMARY]: generateColor(primaryColor),
    [MAIN_COLOR_PALETTE.SECONDARY]: generateColor(secondaryColor),
    [MAIN_COLOR_PALETTE.TERTIARY]: generateColor(tertiaryColor),
    [MAIN_COLOR_PALETTE.QUATERNARY]: generateColor(quaternaryColor),
    [MAIN_COLOR_PALETTE.NEUTRAL]: generateColor(DEFAULT_LIGHT_THEME_NEUTRAL_COLOR),
  };
}

/** This method will prepend any chosen font to a predefined set of default fonts. */
function generateFontFamily(primaryFontFamily: string): string {
  if (!primaryFontFamily || primaryFontFamily === DEFAULT_FONT_FAMILY) {
    return DEFAULT_FONT_FAMILY;
  }

  return `${primaryFontFamily},${DEFAULT_FONT_FAMILY}`;
}

/**
 * This method will generate multiple font color gradations, multiple font-sizes and a font-family string from the
 * provided primary values.
 */
function generateTypographyOptions(
  primaryFontFamily: string = DEFAULT_FONT_FAMILY,
  primaryFontSize: number = DEFAULT_FONT_SIZE,
  primaryFontColor: string = DEFAULT_LIGHT_THEME_FONT_COLOR,
  rtlEnabled = false,
): TypographyOptions {
  return {
    color: {
      [TEXT_COLOR_PALETTE.PRIMARY]: primaryFontColor,
      [TEXT_COLOR_PALETTE.SECONDARY]: tint(0.2, primaryFontColor),
      [TEXT_COLOR_PALETTE.TERTIARY]: tint(0.84, primaryFontColor),
      [TEXT_COLOR_PALETTE.QUATERNARY]: tint(0.98, primaryFontColor),
    },
    font: generateFontFamily(primaryFontFamily),
    rtlEnabled,
    scaleFactor: primaryFontSize / DEFAULT_FONT_SIZE, // TODO (EUD-55) use this factor to scale icons, arrows, history symbols
    size: {
      [TEXT_SIZE_PALETTE.LARGE]: `${Math.round(primaryFontSize * 1.15)}px`,
      [TEXT_SIZE_PALETTE.MEDIUM]: `${Math.round(primaryFontSize)}px`,
      [TEXT_SIZE_PALETTE.SMALL]: `${Math.round(primaryFontSize * 0.85)}px`,
      [TEXT_SIZE_PALETTE.XSMALL]: `${Math.round(primaryFontSize * 0.72)}px`,
    },
    weight: {
      bold: FONT_WEIGHT_BOLD,
      light: FONT_WEIGHT_LIGHT,
      regular: FONT_WEIGHT_REGULAR,
      semiBold: FONT_WEIGHT_SEMI_BOLD,
    },
  };
}

const getPxValue = (pxValue: string | number | undefined): string => {
  if (pxValue === 'auto') {
    return pxValue;
  }

  if ((typeof pxValue === 'number' && pxValue !== 0) || (typeof pxValue === 'string' && !pxValue.includes('px'))) {
    return `${pxValue}px`;
  }

  if (typeof pxValue === 'string') {
    return pxValue;
  }

  return '0';
};

/**
 * Get the right css style as a string based on RTL Enabled
 *
 */
export const getCssBasedOnRtl = (
  theme: ThemeProps,
  propertyType: CSS_PROPERTY,
  pxValue?: number | string,
  borderType?: CSS_BORDER_TYPE,
  color?: string,
): string => {
  const px = getPxValue(pxValue);
  const cachedColor = color || '';
  let borderString = '';

  if (borderType) {
    borderString =
      borderType !== CSS_BORDER_TYPE.NONE ? `${px} ${borderType} ${cachedColor}` : `${CSS_BORDER_TYPE.NONE}`;
  }

  const rtl = theme.typography.rtlEnabled;

  switch (propertyType) {
    case CSS_PROPERTY.TEXT_ALIGNMENT_LEFT:
      return rtl ? `${CSS_PROPERTY.TEXT_ALIGNMENT_RIGHT}` : `${CSS_PROPERTY.TEXT_ALIGNMENT_LEFT}`;
    case CSS_PROPERTY.TEXT_ALIGNMENT_RIGHT:
      return rtl ? `${CSS_PROPERTY.TEXT_ALIGNMENT_LEFT}` : `${CSS_PROPERTY.TEXT_ALIGNMENT_RIGHT}`;
    case CSS_PROPERTY.FLOAT_LEFT:
      return rtl ? `${CSS_PROPERTY.FLOAT_RIGHT}` : `${CSS_PROPERTY.FLOAT_LEFT}`;
    case CSS_PROPERTY.FLOAT_RIGHT:
      return rtl ? `${CSS_PROPERTY.FLOAT_LEFT}` : `${CSS_PROPERTY.FLOAT_RIGHT}`;
    case CSS_PROPERTY.PADDING_LEFT:
      return rtl ? `${CSS_PROPERTY.PADDING_RIGHT} ${px}` : `${CSS_PROPERTY.PADDING_LEFT} ${px}`;
    case CSS_PROPERTY.PADDING_RIGHT:
      return rtl ? `${CSS_PROPERTY.PADDING_LEFT} ${px}` : `${CSS_PROPERTY.PADDING_RIGHT} ${px}`;
    case CSS_PROPERTY.MARGIN_LEFT:
      return rtl ? `${CSS_PROPERTY.MARGIN_RIGHT} ${px}` : `${CSS_PROPERTY.MARGIN_LEFT} ${px}`;
    case CSS_PROPERTY.MARGIN_RIGHT:
      return rtl ? `${CSS_PROPERTY.MARGIN_LEFT} ${px}` : `${CSS_PROPERTY.MARGIN_RIGHT} ${px}`;
    case CSS_PROPERTY.BORDER_LEFT:
      return rtl ? `${CSS_PROPERTY.BORDER_RIGHT} ${borderString};` : `${CSS_PROPERTY.BORDER_LEFT} ${borderString}`;
    case CSS_PROPERTY.BORDER_RIGHT:
      return rtl ? `${CSS_PROPERTY.BORDER_LEFT} ${borderString};` : `${CSS_PROPERTY.BORDER_RIGHT} ${borderString}`;
    case CSS_PROPERTY.BORDER_RADIUS_BOTTOM_LEFT:
      return rtl
        ? `${CSS_PROPERTY.BORDER_RADIUS_BOTTOM_RIGHT} ${px}`
        : `${CSS_PROPERTY.BORDER_RADIUS_BOTTOM_LEFT} ${px}`;
    case CSS_PROPERTY.BORDER_RADIUS_BOTTOM_RIGHT:
      return rtl
        ? `${CSS_PROPERTY.BORDER_RADIUS_BOTTOM_LEFT} ${px}`
        : `${CSS_PROPERTY.BORDER_RADIUS_BOTTOM_RIGHT} ${px}`;
    case CSS_PROPERTY.BORDER_RADIUS_TOP_LEFT:
      return rtl ? `${CSS_PROPERTY.BORDER_RADIUS_TOP_RIGHT} ${px}` : `${CSS_PROPERTY.BORDER_RADIUS_TOP_LEFT} ${px}`;
    case CSS_PROPERTY.BORDER_RADIUS_TOP_RIGHT:
      return rtl ? `${CSS_PROPERTY.BORDER_RADIUS_TOP_LEFT} ${px}` : `${CSS_PROPERTY.BORDER_RADIUS_TOP_RIGHT} ${px}`;
    case CSS_PROPERTY.OBJECT_POSITION_LEFT:
      return rtl ? `${CSS_PROPERTY.OBJECT_POSITION_RIGHT} ${px}` : `${CSS_PROPERTY.OBJECT_POSITION_LEFT} ${px}`;
    case CSS_PROPERTY.OBJECT_POSITION_RIGHT:
      return rtl ? `${CSS_PROPERTY.OBJECT_POSITION_LEFT} ${px}` : `${CSS_PROPERTY.OBJECT_POSITION_RIGHT} ${px}`;
    case CSS_PROPERTY.POSITION_RIGHT:
      return rtl ? `${CSS_PROPERTY.POSITION_LEFT} ${px}` : `${CSS_PROPERTY.POSITION_RIGHT} ${px}`;
    case CSS_PROPERTY.POSITION_LEFT:
      return rtl ? `${CSS_PROPERTY.POSITION_RIGHT} ${px}` : `${CSS_PROPERTY.POSITION_LEFT} ${px}`;
    default:
      return '';
  }
};

export const getTextColor = (defaultColor: string, colorPalette: TextColorPalette, color?: string | null): Color => {
  if (color) {
    return generateColor(color);
  }

  return generateColor(getHighContrastTextColor(defaultColor, colorPalette));
};

export const generateButtonsThemeColors = (
  { neutral, primary }: ColorPalette,
  { color: typographyColor }: TypographyOptions,
  customization?: Customization,
): ButtonsTheme => {
  return {
    accept: {
      bg: generateColor(customization?.color.acceptBtn.backgroundColor || primary.default),
      text: getTextColor(primary.default, typographyColor, customization?.color.acceptBtn.textColor),
    },
    borderRadius: customization?.button.borderRadius || DEFAULT_BORDER_RADIUS,
    deny: {
      bg: generateColor(customization?.color.denyButton.backgroundColor || neutral.default),
      text: getTextColor(neutral.default, typographyColor, customization?.color.denyButton.textColor),
    },
    save: {
      bg: generateColor(customization?.color.saveButton.backgroundColor || neutral.default),
      text: getTextColor(neutral.default, typographyColor, customization?.color.saveButton.textColor),
    },
  };
};

const getDefaultOrCustom = (
  rtlEnabled: boolean,
  customization?: Customization,
): { color: ColorPalette; typography: TypographyOptions } => {
  if (!customization) {
    return {
      color: generateColorPalette(DEFAULT_LIGHT_THEME_ACCENT_COLOR),
      typography: generateTypographyOptions(DEFAULT_FONT_FAMILY, DEFAULT_FONT_SIZE, DEFAULT_LIGHT_THEME_FONT_COLOR),
    };
  }

  return {
    color: generateColorPalette(customization.color.primary),
    typography: generateTypographyOptions(
      customization.font.family,
      customization.font.size,
      DEFAULT_LIGHT_THEME_FONT_COLOR,
      rtlEnabled,
    ),
  };
};

const generateUiTheme = (rtlEnabled: boolean, customization?: Customization): UITheme => {
  const { color, typography } = getDefaultOrCustom(rtlEnabled, customization);
  const buttons = generateButtonsThemeColors(color, typography, customization);

  return {
    buttons,
    firstLayer: {
      secondLayerTrigger: customization?.firstLayer.secondLayerTrigger || SECOND_LAYER_TRIGGER.LINK,
    },
    layer: {
      backgroundColor: customization?.color.layerBackground || DEFAULT_LAYER_COLOR,
      borderRadius: customization?.layer.borderRadius || DEFAULT_LAYER_BORDER_RADIUS,
    },
    links: {
      fontColor: customization?.color.linkFontColor || DEFAULT_LIGHT_THEME_FONT_COLOR,
      iconColor: customization?.color.linkIconColor || DEFAULT_LIGHT_THEME_FONT_COLOR,
    },
    overlay: {
      color: customization?.color.overlayColor || DEFAULT_OVERLAY_COLOR,
      opacity: customization?.overlay.opacity || DEFAULT_OVERLAY_OPACITY,
    },
    text: {
      color: customization?.color.textFontColor || color.primary.default,
    },
    toggle: {
      active: {
        backgroundColor: customization?.toggle.active.backgroundColor || DEFAULT_ACTIVE_TOGGLE,
        iconColor: customization?.toggle.active.iconColor || DEFAULT_TOGGLE_ICON,
      },
      disabled: {
        backgroundColor: customization?.toggle.disabled.backgroundColor || DEFAULT_DISABLED_TOGGLE,
        iconColor: customization?.toggle.disabled.iconColor || DEFAULT_TOGGLE_ICON,
      },
      inactive: {
        backgroundColor: customization?.toggle.inactive.backgroundColor || DEFAULT_INACTIVE_TOGGLE,
        iconColor: customization?.toggle.inactive.iconColor || DEFAULT_TOGGLE_ICON,
      },
    },
    useBackgroundShadow: true,
  };
};

/**
 * This method creates a new variation of the LightTheme, based on the custom primary color, font-size and font-family
 * provided in the UI settings.
 */
export function generateTheme(
  configuration: CcpaUISettings | DefaultUISettings | TCFUISettings,
  rtlEnabled: boolean,
  uiVariant: UI_VARIANT,
  uiSizes?: UISizes,
): ThemeProps {
  const { customization } = configuration;
  const { color, typography } = getDefaultOrCustom(rtlEnabled, customization);
  const sizes = uiSizes || {
    maxBannerWidth: DEFAULT_MAX_BANNER_WIDTH,
    maxSideAndCenterContainerWidth: DEFAULT_MAX_SIDE_AND_CENTER_CONTAINER_WIDTH,
  };

  return { color, spacing, typography, ui: generateUiTheme(rtlEnabled, customization), uiSizes: sizes, uiVariant };
}

// Default LightTheme that uses our default primary color, font-size, and font-family. Other LightTheme variations can
// be created with the generateTheme method.
const { color: defaultColor, typography: defaultTypography } = getDefaultOrCustom(false);

const LightTheme: ThemeProps = {
  color: defaultColor,
  spacing,
  typography: defaultTypography,
  ui: generateUiTheme(false),
  uiSizes: {
    maxBannerWidth: DEFAULT_MAX_BANNER_WIDTH,
    maxSideAndCenterContainerWidth: DEFAULT_MAX_SIDE_AND_CENTER_CONTAINER_WIDTH,
  },
  uiVariant: UI_VARIANT.DEFAULT,
};

export default LightTheme;
